/**
 * v2 servicing Slice 8: staff inbox for borrower portal requests, moved
 * from `customers/portal-requests` (orphaned, no nav entry) to
 * `bhph/portal-requests` (BHPH is the correct home — these are BHPH
 * customers' requests, a servicing concern, not generic CRM). React-query
 * powered; D5 lean columns (borrower + account, subject, type, status, last
 * activity + who spoke last); row expands into the thread detail.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { StaffPortalRequest, staffPortalRequestsApi } from '../../services/staffPortalRequestsApi';
import StaffRequestThread from './components/StaffRequestThread';

const lastSpokeLabel = (author?: StaffPortalRequest['last_message_author'] | null) =>
  author === 'staff' ? 'Staff' : author === 'borrower' ? 'Borrower' : '—';

const PortalRequestsInboxPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [statusFilter, setStatusFilter] = useState('');
  const [includeArchived, setIncludeArchived] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // v2 servicing Slice 11 (D6): the per-account trace links here with
  // ?customer_id=... -- honor it as a pre-applied filter instead of
  // silently dropping the deep-link's context.
  const customerIdFilter = searchParams.get('customer_id') || undefined;

  const filters = {
    status: (statusFilter || undefined) as StaffPortalRequest['status'] | undefined,
    include_archived: includeArchived,
    customer_id: customerIdFilter,
  };

  const {
    data: rows = [],
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['staffPortalRequests', filters],
    queryFn: () => staffPortalRequestsApi.list(filters),
  });

  const { data: counts } = useQuery({
    queryKey: ['staffPortalRequestCounts'],
    queryFn: () => staffPortalRequestsApi.counts(),
    refetchInterval: 30000,
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['staffPortalRequests'] });
    queryClient.invalidateQueries({ queryKey: ['staffPortalRequestCounts'] });
  };

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            Portal Requests
          </h1>
          {counts && (
            <p className="text-sm text-gray-500">{counts.awaiting_staff} awaiting staff response</p>
          )}
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-xs text-gray-500">
            <input
              type="checkbox"
              checked={includeArchived}
              onChange={(e) => setIncludeArchived(e.target.checked)}
              className="rounded border-gray-300"
            />
            Show archived
          </label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
          >
            <option value="">All statuses</option>
            <option value="open">Open</option>
            <option value="in_review">In review</option>
            <option value="resolved">Resolved</option>
            <option value="closed">Closed</option>
          </select>
        </div>
      </div>
      {customerIdFilter && (
        <div className="bg-blue-50 border border-blue-200 text-blue-800 px-4 py-2 rounded-lg flex items-center justify-between">
          <span>Filtered to one customer&apos;s requests.</span>
          <button onClick={() => setSearchParams({})} className="text-sm font-medium underline">
            Clear filter
          </button>
        </div>
      )}
      {isError && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-lg">
          Unable to load portal requests.
        </div>
      )}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50 dark:bg-gray-900">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Borrower
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Type
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Subject
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Status
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Last Activity
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Last Spoke
              </th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {rows.map((row) => (
              <React.Fragment key={row.id}>
                <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                    <div>{row.customer_name || '—'}</div>
                    {row.account_id && row.account_number && (
                      <Link
                        to={`/bhph/account/${row.account_id}`}
                        className="text-xs text-blue-600 hover:text-blue-800"
                      >
                        {row.account_number}
                      </Link>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                    {row.request_type}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                    {row.subject}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">{row.status}</td>
                  <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                    {row.last_message_at ? new Date(row.last_message_at).toLocaleString() : '-'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {lastSpokeLabel(row.last_message_author)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => setExpandedId(expandedId === row.id ? null : row.id)}
                      className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                    >
                      {expandedId === row.id ? 'Hide' : 'Respond'}
                    </button>
                  </td>
                </tr>
                {expandedId === row.id && (
                  <tr>
                    <td colSpan={7} className="p-0">
                      <StaffRequestThread
                        request={row}
                        onUpdated={refresh}
                        onArchiveToggle={() => {
                          refresh();
                          setExpandedId(null);
                        }}
                      />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
            {!isLoading && rows.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-6 text-center text-sm text-gray-500">
                  No portal requests.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PortalRequestsInboxPage;
